import {
  DESIGN_SCHEMA_VERSION,
  type HeaderComponent,
  type SavedDesign,
} from '@rh/shared/schemas/design'
import { escapeHtml } from '@rh/shared/templates/design-tokens'
import { suggestionToken } from './classify'
import type {
  ExtractedBand,
  ExtractedHeaderModel,
  ExtractedImage,
  ExtractedLine,
  TokenSuggestion,
} from './types'
import { DESIGN_PAGE } from './text-extractor'

// ============================================================
// IR → SavedDesign assembly. Pure: image asset keys are resolved
// by the caller (uploads happen before this runs).
// ============================================================

const MARGIN = 17
const TEXT_LINE_HEIGHT = 1.3
/** Baseline slack so the last line never clips. */
const HEIGHT_SLACK = 2
/** Text blocks get the editor's own 2px/4px padding compensated. */
const TEXT_PAD_X = 4
const TEXT_PAD_Y = 2

let idCounter = 0
function nextId(prefix: string): string {
  idCounter += 1
  return `import-${prefix}-${Date.now().toString(36)}-${idCounter}`
}

// Canvas text measurement, cached. jsdom has no 2d context — there (and
// only there) this returns 0 and components keep their extracted bbox
// width, which is what the unit tests assert against.
let measureCtx: CanvasRenderingContext2D | null | undefined
function measureTextWidth(text: string, font: string): number {
  if (measureCtx === undefined) {
    measureCtx =
      typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d')
        : null
  }
  if (!measureCtx) return 0
  measureCtx.font = font
  return measureCtx.measureText(text).width
}

/**
 * Splices linked token suggestions into a line's escaped text. Literal
 * segments are HTML-escaped; tokens are inserted verbatim (the renderer
 * resolves and escapes their values at render time).
 */
export function lineContent(
  line: ExtractedLine,
  linked: TokenSuggestion[]
): string {
  // Work on the plain text; bold/italic runs are reapplied only when the
  // whole line shares one style (mixed-style tokenized lines aren't worth
  // the complexity — the editor can restyle).
  let html = ''
  let rest = line.text
  // Apply longest matches first so overlapping suggestions can't corrupt
  // shorter ones.
  const ordered = [...linked].sort(
    (a, b) => b.matchedText.length - a.matchedText.length
  )
  const applied: Array<{ index: number; length: number; token: string }> = []
  for (const s of ordered) {
    const token = suggestionToken(s)
    if (!token) continue
    const idx = rest.indexOf(s.matchedText)
    if (idx === -1) continue
    // Mask the matched span so later (shorter) matches can't hit inside it.
    applied.push({ index: idx, length: s.matchedText.length, token })
    rest =
      rest.slice(0, idx) +
      ' '.repeat(s.matchedText.length) +
      rest.slice(idx + s.matchedText.length)
  }
  applied.sort((a, b) => a.index - b.index)
  let cursor = 0
  for (const a of applied) {
    html += escapeHtml(line.text.slice(cursor, a.index))
    html += a.token
    cursor = a.index + a.length
  }
  html += escapeHtml(line.text.slice(cursor))

  const bold = line.runs.length > 0 && line.runs.every(r => r.bold)
  const italic = line.runs.length > 0 && line.runs.every(r => r.italic)
  if (bold) html = `<b>${html}</b>`
  if (italic) html = `<i>${html}</i>`
  return html
}

function bandComponents(
  band: ExtractedBand,
  bandIndex: number,
  suggestions: TokenSuggestion[],
  logoAssetKeys: Map<ExtractedImage, string>
): HeaderComponent[] {
  const components: HeaderComponent[] = []

  const fillContainer = (
    rect: { x: number; y: number; width: number; height: number },
    color: string,
    radius: number
  ): HeaderComponent => ({
    id: nextId('band'),
    type: 'container',
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    rotation: 0,
    backgroundColor: color,
    borderLinked: true,
    borderTop: { width: 0, style: 'solid', color },
    borderRight: { width: 0, style: 'solid', color },
    borderBottom: { width: 0, style: 'solid', color },
    borderLeft: { width: 0, style: 'solid', color },
    radiusLinked: true,
    radiusTL: radius,
    radiusTR: radius,
    radiusBR: radius,
    radiusBL: radius,
    padding: 0,
  })

  if (band.backgroundColor) {
    components.push(fillContainer(band.bbox, band.backgroundColor, 0))
  }
  // Partial-band background shapes (banners), behind images and text.
  for (const shape of band.shapes ?? []) {
    components.push(fillContainer(shape.bbox, shape.color, shape.radius))
  }

  for (const image of band.images) {
    const assetKey = logoAssetKeys.get(image)
    if (!assetKey) continue
    components.push({
      id: nextId('logo'),
      type: 'image',
      x: Math.round(image.bbox.x),
      y: Math.round(image.bbox.y),
      width: Math.round(image.bbox.width),
      height: Math.round(image.bbox.height),
      rotation: 0,
      assetKey,
      originalWidth: image.naturalWidth,
      originalHeight: image.naturalHeight,
      fit: 'contain',
    })
  }

  band.blocks.forEach((block, blockIndex) => {
    if (!block.included) return
    const linkedByLine = new Map<number, TokenSuggestion[]>()
    for (const s of suggestions) {
      if (
        s.bandIndex !== bandIndex ||
        s.blockIndex !== blockIndex ||
        suggestionToken(s) === null
      )
        continue
      const list = linkedByLine.get(s.lineIndex) ?? []
      list.push(s)
      linkedByLine.set(s.lineIndex, list)
    }
    const content = block.lines
      .map((line, li) => lineContent(line, linkedByLine.get(li) ?? []))
      .join('<br/>')
    if (!content) return
    const fontSize = Math.max(1, Math.round(block.fontSizePx))
    // The extracted bbox measures the SOURCE font; the editor renders the
    // MAPPED font, whose metrics differ — text sized to the bbox can wrap.
    // Measure each line as the editor will render it and widen when needed.
    const measuredMax = Math.max(
      0,
      ...block.lines.map(line => {
        const bold = line.runs.length > 0 && line.runs.every(r => r.bold)
        return measureTextWidth(
          line.text,
          `${bold ? '700' : '400'} ${fontSize}px ${block.fontFamily}`
        )
      })
    )
    const growth = Math.max(0, Math.ceil(measuredMax) - block.bbox.width)
    // Width slack: token substitution can produce longer values than the
    // literal text they replace. Like the remap growth, it must extend
    // AWAY from the aligned edge, or a longer value shifts (or overflows)
    // past the alignment line the original document established.
    const WIDTH_SLACK = 12
    const extra = WIDTH_SLACK + growth
    const slackLeft =
      block.align === 'right' ? extra : block.align === 'center' ? extra / 2 : 0
    components.push({
      id: nextId('text'),
      type: 'text',
      x: Math.round(block.bbox.x - TEXT_PAD_X - slackLeft),
      y: Math.round(block.bbox.y - TEXT_PAD_Y),
      width: Math.round(block.bbox.width + TEXT_PAD_X * 2 + extra),
      height: Math.round(block.bbox.height + TEXT_PAD_Y * 2 + HEIGHT_SLACK),
      rotation: 0,
      content,
      fontSize,
      fontFamily: block.fontFamily,
      textAlign: block.align,
      lineHeight: TEXT_LINE_HEIGHT,
      color: block.colorHex,
    })
  })

  return components
}

/**
 * Assembles the final SavedDesign from the reviewed extraction model. The
 * resume block fills the space between the bands, mirroring
 * convertCustomizeToDesign's geometry.
 */
export function buildDesignFromExtraction(
  model: ExtractedHeaderModel,
  logoAssetKeys: Map<ExtractedImage, string>
): SavedDesign {
  idCounter = 0
  const header = model.bands.find(b => b.region === 'header')
  const footer = model.bands.find(b => b.region === 'footer')

  const components: HeaderComponent[] = []
  model.bands.forEach(band => {
    components.push(
      ...bandComponents(
        band,
        model.bands.indexOf(band),
        model.suggestions,
        logoAssetKeys
      )
    )
  })

  const resumeTop = header ? header.bbox.y + header.bbox.height : MARGIN
  const resumeBottom = footer ? footer.bbox.y : DESIGN_PAGE.h - MARGIN
  components.unshift({
    id: nextId('resume'),
    type: 'resume',
    x: MARGIN,
    y: Math.round(resumeTop),
    width: DESIGN_PAGE.w - MARGIN * 2,
    height: Math.max(Math.round(resumeBottom - resumeTop), 1),
    rotation: 0,
  })

  return {
    version: DESIGN_SCHEMA_VERSION,
    pageSize: 'letter',
    pageMode: 'all',
    firstPage: { components },
    subsequentPages: { components },
  }
}
